<template>
  <el-dialog
    :model-value="open"
    @update:model-value="emit('update:open', $event)"
    :title="title"
    width="700px"
    :close-on-click-modal="false"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="120px"
      v-loading="loading"
    >
      <el-form-item label="标准名称" prop="standardName">
        <el-input v-model="form.standardName" placeholder="请输入标准名称" />
      </el-form-item>
      <el-form-item label="标准编号" prop="standardNumber">
        <el-input v-model="form.standardNumber" placeholder="请输入标准号" />
      </el-form-item>
      <el-form-item label="归口单位" prop="responsibleUnit">
        <el-input v-model="form.responsibleUnit" placeholder="请输入归口单位" />
      </el-form-item>
      <el-form-item label="发布部门" prop="departmentName">
        <el-input
          v-model="form.departmentName"
          placeholder="请输入发布部门名称"
        />
      </el-form-item>
      <el-form-item label="起草人" prop="draftAuthor">
        <el-input v-model="form.draftAuthor" placeholder="请输入起草人" />
      </el-form-item>
      <el-form-item label="起草单位" prop="draftUnit">
        <el-input v-model="form.draftUnit" placeholder="请输入起草单位" />
      </el-form-item>
      <el-form-item label="适用范围" prop="scope">
        <el-input
          v-model="form.scope"
          type="textarea"
          rows="3"
          placeholder="请输入适用范围"
        />
      </el-form-item>
      <el-form-item label="标准级别" prop="level">
        <el-select
          v-model="form.level"
          placeholder="请选择或输入级别"
          filterable
          allow-create
          clearable
          :loading="levelLoading"
          @visible-change="handleSelectVisible"
          @change="handleLevelChange"
        >
          <el-option
            v-for="level in levelOptions"
            :key="level.value"
            :label="level.label"
            :value="level.value"
          />
          <!-- 如果当前值不在选项中，也显示为一个选项 -->
          <el-option
            v-if="
              form.level &&
              !levelOptions.map((l) => l.value).includes(form.level)
            "
            :key="form.level"
            :label="`${form.level} (自定义)`"
            :value="form.level"
          />
        </el-select>
        <div
          v-if="
            form.level && !levelOptions.map((l) => l.value).includes(form.level)
          "
          class="custom-level-hint"
        >
          <i class="fas fa-info-circle"></i> 当前为自定义级别
        </div>
      </el-form-item>
      <el-form-item label="规范性引用文件" prop="normativeDocuments">
        <el-input
          v-model="form.normativeDocuments"
          type="textarea"
          rows="3"
          placeholder="请输入规范性引用文件内容"
        />
      </el-form-item>
      <el-form-item label="发布日期" prop="publicationDate" required>
        <el-date-picker
          v-model="form.publicationDate"
          type="datetime"
          placeholder="请选择发布日期"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
        />
      </el-form-item>
      <el-form-item label="实施日期" prop="implementationDate" required>
        <el-date-picker
          v-model="form.implementationDate"
          type="datetime"
          placeholder="请选择实施日期"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
        />
      </el-form-item>
      <el-form-item label="图片">
        <MultipleUploadComponent
          ref="multiUploadRef"
          v-model="form.imageList"
          @change="handleImagesChange"
        />
      </el-form-item>
      <el-form-item label="相关文件" v-if="props.editData">
        <FileUploadComponent
          ref="fileUploadRef"
          v-model="fileDatas"
          :standardId="form.id"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="cancel">取消</el-button>
        <el-button type="primary" @click="handleConfirm">确定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ElMessage } from "element-plus";
import { ref, toRef, watch, computed, onMounted } from "vue";
import {
  addStandard,
  updateStandard,
  listStandardByPage,
  getStandardLevels,
} from "@/api/standard";
import MultipleUploadComponent from "@/components/MultipleUploadComponent.vue";
import FileUploadComponent from "@/components/FileUploadComponent.vue";
import type { FormInstance } from "element-plus";

// 确保 emit 包含 'update:open' 事件
const emit = defineEmits(["update:open", "refreshList"]);

// 定义 props
const props = defineProps({
  title: {
    type: String,
    default: "新增",
  },
  open: {
    type: Boolean,
    default: false,
  },
  editData: {
    // 添加编辑数据的 prop
    type: Object,
    default: () => null,
  },
});

// 表单数据
const formRef = ref();
const multiUploadRef = ref();
const levelOptions = ref<Array<{ value: string; label: string }>>([]);
const levelLoading = ref(false); // 加载状态
const hasLoadedLevels = ref(false); // 记录是否已经加载过级别选项
const customLevels = ref<string[]>([]); // 存储用户自定义的级别

// 确保在所有需要格式化日期的地方使用相同的函数
const formatDateToString = (date: string | Date | null) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}:${String(
    d.getSeconds()
  ).padStart(2, "0")}`;
};
// 表单验证规则
const rules = {
  standardName: [
    { required: true, message: "请输入标准名称", trigger: "blur" },
  ],
  standardNumber: [
    { required: true, message: "请输入标准编号", trigger: "blur" },
  ],
  responsibleUnit: [
    { required: true, message: "请输入归口单位", trigger: "blur" },
  ],
  departmentName: [
    { required: true, message: "请输入发布部门", trigger: "blur" },
  ],
  draftAuthor: [{ required: true, message: "请输入起草人", trigger: "blur" }],
  draftUnit: [{ required: true, message: "请输入起草单位", trigger: "blur" }],
  level: [
    // 添加标准级别的必填验证
    { required: true, message: "请选择标准级别", trigger: "change" },
  ],
  normativeDocuments: [
    // 添加规范性引用文件的必填验证
    { required: true, message: "请输入规范性引用文件内容", trigger: "blur" },
  ],
  publicationDate: [
    { required: true, message: "请选择发布日期", trigger: "change" },
  ],
  implementationDate: [
    { required: true, message: "请选择实施日期", trigger: "change" },
  ],
};
const loading = ref(false);

// 修改 loadLevelOptions 函数
const loadLevelOptions = async () => {
  try {
    levelLoading.value = true;

    // 调用新接口获取级别列表
    const response = await getStandardLevels();

    if (response?.code === 1 && response.data) {
      // 将接口返回的数据直接作为选项
      const levels = response.data;

      // 合并自定义级别
      const allLevels = [...levels];
      customLevels.value.forEach((customLevel) => {
        // 检查自定义级别是否已存在
        if (!allLevels.some((level) => level.value === customLevel)) {
          allLevels.push({ value: customLevel, label: customLevel });
        }
      });

      // 确保有默认选项
      if (allLevels.length === 0) {
        allLevels.push(
          { value: "国家级", label: "国家级" },
          { value: "省级", label: "省级" },
          { value: "市级", label: "市级" },
          { value: "企业级", label: "企业级" }
        );
      }

      levelOptions.value = allLevels;
      hasLoadedLevels.value = true;
    }
  } catch (error) {
    console.error("加载级别选项失败:", error);
    // 如果接口失败，使用默认选项并合并自定义级别
    const allLevels = [
      { value: "国家级", label: "国家级" },
      { value: "省级", label: "省级" },
      { value: "市级", label: "市级" },
      { value: "企业级", label: "企业级" },
    ];
    customLevels.value.forEach((customLevel) => {
      if (!allLevels.some((level) => level.value === customLevel)) {
        allLevels.push({ value: customLevel, label: customLevel });
      }
    });
    levelOptions.value = allLevels;
  } finally {
    levelLoading.value = false;
  }
};

// 处理下拉框显示事件
const handleSelectVisible = (visible: boolean) => {
  if (visible) {
    // 每次打开下拉框都重新加载，确保获取最新数据
    loadLevelOptions();
  }
};
// 处理级别变化事件
const handleLevelChange = (value: string) => {
  if (
    value &&
    !levelOptions.value.some((level) => level.value === value) &&
    !customLevels.value.includes(value)
  ) {
    // 如果是新的自定义级别，添加到自定义级别列表
    customLevels.value.push(value);
    // 同时添加到选项列表中
    levelOptions.value.push({ value: value, label: value });
  }
};
// 修改 form 的定义和初始化
const form = ref({
  id: "",
  standardName: "",
  standardNumber: "",
  responsibleUnit: "",
  departmentName: "",
  draftAuthor: "",
  draftUnit: "",
  scope: "",
  level: "",
  normativeDocuments: "",
  publicationDate: "",
  implementationDate: "",
  type: "",
  image: "",
  imageList: [] as string[],

  file: "",
  fileName: "",
});
const fileDatas = ref({
  standardId: props.editData?.id,
  fileId: props.editData?.fileId,
  fileName: "",
  file: "",
});
// 添加 watch 来监听 editData 的变化
watch(
  () => props.editData,
  (newVal) => {
    if (newVal) {
      // 处理可能的图片列表（逗号分隔的URL字符串）
      let imageList: string[] = [];
      if (newVal.image) {
        // 如果image字段包含逗号，说明是多个图片URL
        imageList = newVal.image.split(",").filter((url) => url.trim() !== "");
      }

      // 当有编辑数据时，更新表单数据
      const formData = {
        id: newVal.id || "",
        standardName: newVal.standardName || "",
        standardNumber: newVal.standardNumber || "",
        responsibleUnit: newVal.responsibleUnit || "",
        departmentName: newVal.departmentName || "",
        draftAuthor: newVal.draftAuthor || "",
        draftUnit: newVal.draftUnit || "",
        scope: newVal.scope || "",
        level: newVal.level || "",
        normativeDocuments: newVal.normativeDocuments || "",
        publicationDate: formatDateToString(newVal.publicationDate) || "",
        implementationDate: formatDateToString(newVal.implementationDate) || "",
        type: newVal.type || "",
        image: imageList.length > 0 ? imageList[0] : "",
        imageList: imageList,
        fileId: newVal.fileId || "",
        file: newVal.file || "",
        fileName: newVal.fileName || "",
      };

      // 如果编辑数据中有自定义级别，确保它出现在选项中
      if (
        newVal.level &&
        !levelOptions.value.some((level) => level.value === newVal.level)
      ) {
        customLevels.value.push(newVal.level);
        levelOptions.value.push({ value: newVal.level, label: newVal.level });
      }

      // 使用新对象更新表单
      form.value = formData;
    } else {
      // 当没有编辑数据时，重置表单
      form.value = {
        id: "",
        standardName: "",
        standardNumber: "",
        responsibleUnit: "",
        departmentName: "",
        draftAuthor: "",
        draftUnit: "",
        scope: "",
        level: "",
        normativeDocuments: "",
        publicationDate: "",
        implementationDate: "",
        type: "",
        image: "",
        imageList: [],
        file: "",
        fileName: "",
      };
      fileDatas.value = {
        standardId: props.editData?.id,
        fileId: "",
        fileName: "",
        file: "",
      };
      if (multiUploadRef.value) {
        multiUploadRef.value.clearFiles();
      }
    }
  },
  { immediate: true }
);

// 监听对话框打开状态
watch(
  () => props.open,
  (newVal) => {
    if (newVal) {
      // 对话框打开时，重新加载级别选项
      loadLevelOptions();
    }
  }
);

// 添加处理多图上传变化的方法
const handleImagesChange = (urls: string[]) => {
  form.value.imageList = urls;
  // 保持单图兼容性
  form.value.image = urls.length > 0 ? urls[0] : "";
};

/** 提交表单 */
const handleConfirm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) {
      ElMessage.warning("请填写必填项");
      return;
    }

    try {
      loading.value = true;
      const submitData = {
        id: props.editData?.id,
        standardName: form.value.standardName,
        level: form.value.level || "",
        standardNumber: form.value.standardNumber || "",
        responsibleUnit: form.value.responsibleUnit || "",
        publicationDate: formatDateToString(form.value.publicationDate) || "",
        implementationDate:
          formatDateToString(form.value.implementationDate) || "",
        scope: form.value.scope || "",
        departmentName: form.value.departmentName || "",
        draftAuthor: form.value.draftAuthor || "",
        draftUnit: form.value.draftUnit || "",
        normativeDocuments: form.value.normativeDocuments || "",
        type: form.value.type || "",
        imageList: form.value.imageList || [],
        image:
          form.value.imageList && form.value.imageList.length > 0
            ? form.value.imageList[0]
            : "",
      };
      const fileDatas = {
        standardId: props.editData?.id,
        file: form.value.file || "",
        fileName: form.value.fileName || "",
        fileId: form.value.fileId || "",
      };

      let response;
      let fileResponse;
      if (props.editData) {
        response = await updateStandard(submitData);
        // fileResponse = await uploadFileWithParams(fileDatas)
      } else {
        response = await addStandard(submitData);
        // fileResponse = await updateFile(fileDatas)
      }

      if (response?.code === 1) {
        ElMessage.success(props.editData ? "更新标准成功" : "新增标准成功");
        emit("refreshList");
        // 如果是自定义级别且成功保存，确保它出现在选项中
        if (
          form.value.level &&
          !levelOptions.value.some((level) => level.value === form.value.level)
        ) {
          customLevels.value.push(form.value.level);
          levelOptions.value.push({
            value: form.value.level,
            label: form.value.level,
          });
        }

        resetForm(formRef.value);
        // if(fileResponse?.code === 1){
        //   ElMessage.success(props.editData ? "更新文件成功" : "新增文件成功")
        // }
        if (multiUploadRef.value) {
          multiUploadRef.value.clearFiles();
        }
        // 关闭对话框
        emit("update:open", false);
      } else {
        ElMessage.error(
          response?.msg || (props.editData ? "更新标准失败" : "新增标准失败")
        );
      }
    } catch (error) {
      console.error(props.editData ? "更新标准失败:" : "新增标准失败:", error);
      ElMessage.error(props.editData ? "更新标准失败" : "新增标准失败");
    } finally {
      loading.value = false;
    }
  });
};

// 重置表单
const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.resetFields();
  // 清除上传组件的图片
  if (multiUploadRef.value) {
    multiUploadRef.value.clearFiles();
  }
  // 清空所有表单数据
  form.value = {
    standardName: "",
    standardNumber: "",
    responsibleUnit: "",
    departmentName: "",
    draftAuthor: "",
    draftUnit: "",
    scope: "",
    level: "",
    normativeDocuments: "",
    publicationDate: "",
    implementationDate: "",
    type: "",
    image: "",
    imageList: [],
    file: "",
    fileName: "",
  };
};

// 取消操作
const cancel = () => {
  // 重置表单
  resetForm(formRef.value);
  // 清除上传组件的图片
  if (multiUploadRef.value) {
    multiUploadRef.value.clearFiles();
  }
  // 触发父组件关闭对话框的事件
  emit("update:open", false);
};
</script>
